// ============================================================================
// PROJECT REQUIREMENTS TAB
// Persona-driven layout: sidebar persona list + detail panel with stories
// ============================================================================

import React, { useState, useMemo } from 'react';
import {
  Users, FileText, AlertTriangle, Sparkles, RefreshCw,
  ChevronDown, ChevronUp, Settings, Lightbulb,
  CheckCircle2, Search, AlertCircle, Contact2, Target,
} from 'lucide-react';
import {
  useProjectRequirements,
  useSyncRequirements,
  useUpdatePersonaBackstory,
  type ProjectPersona,
  type PersonaBackstory,
} from '../../hooks/useProjectRequirements';
import { PersonaBackstoryModal } from '../PersonaBackstoryModal';

interface RequirementsTabProps {
  projectId: string;
  token: string;
}

type PriorityFilter = 'all' | 'high' | 'medium' | 'low';

const PRIORITY_STYLES: Record<string, { bar: string; badge: string }> = {
  high:   { bar: 'bg-red-500',   badge: 'bg-red-50 text-red-700 border border-red-200' },
  medium: { bar: 'bg-amber-500', badge: 'bg-amber-50 text-amber-700 border border-amber-200' },
  low:    { bar: 'bg-slate-300', badge: 'bg-slate-50 text-slate-600 border border-slate-200' },
};

const PERSONA_COLORS = [
  { bg: 'bg-purple-100', text: 'text-purple-700', ring: 'ring-purple-200' },
  { bg: 'bg-indigo-100', text: 'text-indigo-700', ring: 'ring-indigo-200' },
  { bg: 'bg-teal-100',   text: 'text-teal-700',   ring: 'ring-teal-200' },
  { bg: 'bg-rose-100',   text: 'text-rose-700',   ring: 'ring-rose-200' },
  { bg: 'bg-amber-100',  text: 'text-amber-700',  ring: 'ring-amber-200' },
  { bg: 'bg-cyan-100',   text: 'text-cyan-700',   ring: 'ring-cyan-200' },
];

function getInitials(name: string): string {
  return name.split(/[\s._-]+/).map(w => w[0]).join('').toUpperCase().slice(0, 2);
}

export const RequirementsTab: React.FC<RequirementsTabProps> = ({ projectId, token }) => {
  const { data, isLoading, error } = useProjectRequirements(projectId, token);
  const syncMutation = useSyncRequirements(projectId);
  const backstoryMutation = useUpdatePersonaBackstory(projectId);

  const [selectedPersonaId, setSelectedPersonaId] = useState<string | 'all'>('all');
  const [priorityFilter, setPriorityFilter] = useState<PriorityFilter>('all');
  const [storySearch, setStorySearch] = useState('');
  const [techSpecsExpanded, setTechSpecsExpanded] = useState(false);
  const [assumptionsExpanded, setAssumptionsExpanded] = useState(false);
  const [backstoryPersona, setBackstoryPersona] = useState<ProjectPersona | null>(null);
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const showNotification = (type: 'success' | 'error', message: string) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 5000);
  };

  // Deduplicate personas by name
  const personas = useMemo(() => {
    if (!data) return [];
    const seen = new Map<string, ProjectPersona & { storyCount: number }>();
    for (const p of data.personas) {
      const key = p.name.toLowerCase().trim();
      if (seen.has(key)) {
        const existing = seen.get(key)!;
        for (const g of p.goals) { if (!existing.goals.includes(g)) existing.goals.push(g); }
        for (const pp of p.painPoints) { if (!existing.painPoints.includes(pp)) existing.painPoints.push(pp); }
        if (!existing.backstory && p.backstory) existing.backstory = p.backstory;
      } else {
        const storyCount = data.userStories.filter(s =>
          s.persona.toLowerCase().trim() === key || key.includes(s.persona.toLowerCase().trim())
        ).length;
        seen.set(key, { ...p, storyCount });
      }
    }
    return Array.from(seen.values());
  }, [data]);

  const selectedPersona = useMemo(
    () => selectedPersonaId === 'all' ? null : personas.find(p => p.id === selectedPersonaId) || null,
    [personas, selectedPersonaId]
  );

  const visibleStories = useMemo(() => {
    if (!data) return [];
    let stories = data.userStories;

    if (selectedPersona) {
      const nameKey = selectedPersona.name.toLowerCase().trim();
      stories = stories.filter(s => {
        const sp = s.persona.toLowerCase().trim();
        return sp === nameKey || sp.includes(nameKey) || nameKey.includes(sp);
      });
    }

    if (priorityFilter !== 'all') {
      stories = stories.filter(s => s.priority === priorityFilter);
    }
    if (storySearch.trim()) {
      const q = storySearch.toLowerCase();
      stories = stories.filter(s => s.story.toLowerCase().includes(q) || s.persona.toLowerCase().includes(q));
    }
    return stories;
  }, [data, selectedPersona, priorityFilter, storySearch]);

  const techSpecItems = useMemo(() => {
    if (!data?.technicalSpecs) return [];
    const s = data.technicalSpecs;
    return [...s.coreFunctionality, ...s.integrationPoints, ...s.dataRequirements, ...s.securityConsiderations, ...s.performanceTargets];
  }, [data]);

  const stats = useMemo(() => {
    if (!data) return { personas: 0, stories: 0, highPriority: 0, sessions: 0 };
    return {
      personas: personas.length,
      stories: data.userStories.length,
      highPriority: data.userStories.filter(s => s.priority === 'high').length,
      sessions: data.sessionCount,
    };
  }, [data, personas]);

  const handleSync = () => {
    syncMutation.mutate(undefined, {
      onSuccess: (result) => showNotification('success', `Synced ${result.personaCount} personas and ${result.storyCount} stories from sessions`),
      onError: (err) => showNotification('error', `Sync failed: ${err.message}`),
    });
  };

  const handleSaveBackstory = async (backstory: PersonaBackstory) => {
    if (!backstoryPersona) return;
    await backstoryMutation.mutateAsync({
      sessionId: backstoryPersona.sessionId,
      personaId: backstoryPersona.id,
      backstory,
      name: backstoryPersona.name,
      role: backstoryPersona.role,
    });
  };

  // ── Loading / error ──────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-40">
        <svg className="w-6 h-6 animate-spin text-slate-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-3 p-6 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
        <AlertCircle size={18} />
        Failed to load requirements: {error.message}
      </div>
    );
  }

  const isEmpty = !data || (personas.length === 0 && data.userStories.length === 0 && techSpecItems.length === 0 && data.assumptions.length === 0);

  return (
    <div className="flex flex-col gap-6">
      {/* Action bar */}
      <div className="flex items-center justify-end">
        <button
          onClick={handleSync}
          disabled={syncMutation.isPending}
          className="flex items-center justify-center gap-2 rounded-xl h-10 px-5 bg-primary hover:bg-primary-dark transition-colors text-white text-sm font-bold shadow-sm disabled:opacity-50"
        >
          <RefreshCw size={16} className={syncMutation.isPending ? 'animate-spin' : ''} />
          {syncMutation.isPending ? 'Syncing...' : 'Sync from Sessions'}
        </button>
      </div>

      {/* Stats */}
      {!isEmpty && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard label="Personas" value={stats.personas} icon={<Users size={28} />} iconColor="text-primary" />
          <StatCard label="User Stories" value={stats.stories} icon={<FileText size={28} />} iconColor="text-blue-500" />
          <StatCard label="High Priority" value={stats.highPriority} icon={<AlertTriangle size={28} />} iconColor="text-red-500" />
          <StatCard label="Sessions" value={stats.sessions} icon={<Sparkles size={28} />} iconColor="text-amber-500" />
        </div>
      )}

      {/* Empty state */}
      {isEmpty && (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mb-4">
            <FileText size={28} className="text-slate-400" />
          </div>
          <h3 className="text-lg font-bold text-navy mb-2">No Requirements Yet</h3>
          <p className="text-slate-500 text-sm max-w-md mb-6">
            Click "Sync from Sessions" to pull personas and user stories from linked sessions.
          </p>
        </div>
      )}

      {/* Main two-panel layout */}
      {!isEmpty && (
        <div className="flex gap-5 items-start">

          {/* ── Left: Persona Sidebar ──────────────────────────────────── */}
          {/* Fix 2: w-72 (288px) instead of w-56 — prevents name truncation */}
          {/* Fix 2+5: sticky + max-height + scroll so sidebar never exceeds viewport */}
          <div className="w-72 flex-shrink-0 bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden sticky top-4 max-h-[calc(100vh-160px)] flex flex-col">
            <div className="px-4 py-3 border-b border-[#e2e8f0] flex-shrink-0">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Personas</p>
            </div>

            {/* Scrollable list */}
            <div className="overflow-y-auto flex-1">
              {/* All stories entry */}
              <button
                onClick={() => setSelectedPersonaId('all')}
                className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors border-b border-[#e2e8f0] ${
                  selectedPersonaId === 'all'
                    ? 'bg-primary/5'
                    : 'hover:bg-slate-50'
                }`}
              >
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                  selectedPersonaId === 'all' ? 'bg-primary/10 text-primary' : 'bg-slate-100 text-slate-500'
                }`}>
                  <Target size={13} />
                </div>
                {/* Fix 5: single-line — name only, no role subtitle */}
                <span className={`text-sm font-bold truncate flex-1 ${selectedPersonaId === 'all' ? 'text-primary' : 'text-navy'}`}>
                  All Stories
                </span>
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full flex-shrink-0 ${
                  selectedPersonaId === 'all' ? 'bg-primary/10 text-primary' : 'bg-slate-100 text-slate-500'
                }`}>
                  {data?.userStories.length}
                </span>
              </button>

              {/* Persona list — Fix 5: single-line rows, role in title tooltip */}
              {personas.map((persona, idx) => {
                const color = PERSONA_COLORS[idx % PERSONA_COLORS.length];
                const isActive = selectedPersonaId === persona.id;
                return (
                  <button
                    key={persona.id}
                    onClick={() => setSelectedPersonaId(persona.id)}
                    title={`${persona.name} — ${persona.role}`}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors border-b border-[#e2e8f0] last:border-b-0 ${
                      isActive ? 'bg-primary/5' : 'hover:bg-slate-50'
                    }`}
                  >
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${color.bg} ${color.text}`}>
                      {getInitials(persona.name)}
                    </div>
                    <span className={`text-sm font-semibold truncate flex-1 ${isActive ? 'text-primary' : 'text-navy'}`}>
                      {persona.name}
                    </span>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {persona.backstory?.traits?.length ? (
                        <Contact2 size={10} className="text-violet-400" />
                      ) : null}
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                        isActive ? 'bg-primary/10 text-primary' : 'bg-slate-100 text-slate-500'
                      }`}>
                        {(persona as any).storyCount}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* ── Right: Detail Panel ────────────────────────────────────── */}
          <div className="flex-1 min-w-0 flex flex-col gap-5">

            {/* Selected persona card */}
            {selectedPersona && (() => {
              const idx = personas.findIndex(p => p.id === selectedPersona.id);
              const color = PERSONA_COLORS[idx % PERSONA_COLORS.length];
              return (
                <div className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">
                  {/* Persona header */}
                  <div className="flex items-start justify-between gap-4 p-5 border-b border-[#e2e8f0]">
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0 ${color.bg} ${color.text}`}>
                        {getInitials(selectedPersona.name)}
                      </div>
                      <div>
                        <h2 className="text-lg font-bold text-navy leading-tight">{selectedPersona.name}</h2>
                        <p className="text-sm text-slate-500">{selectedPersona.role}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setBackstoryPersona(selectedPersona)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl border text-xs font-bold transition-colors flex-shrink-0 ${
                        selectedPersona.backstory?.traits?.length || selectedPersona.backstory?.background
                          ? 'border-violet-200 bg-violet-50 text-violet-700 hover:bg-violet-100'
                          : 'border-[#e2e8f0] bg-white text-slate-500 hover:bg-slate-50'
                      }`}
                    >
                      <Contact2 size={13} />
                      {selectedPersona.backstory ? 'Edit Backstory' : 'Add Backstory'}
                    </button>
                  </div>

                  {/* Persona detail */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-0 divide-y md:divide-y-0 md:divide-x divide-[#e2e8f0]">
                    {/* About */}
                    <div className="p-5">
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">About</p>
                      {selectedPersona.context ? (
                        <p className="text-sm text-slate-700 leading-relaxed">{selectedPersona.context}</p>
                      ) : (
                        <p className="text-sm text-slate-400 italic">No description.</p>
                      )}
                    </div>
                    {/* Goals */}
                    <div className="p-5">
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Goals</p>
                      {selectedPersona.goals.length > 0 ? (
                        <ul className="space-y-1">
                          {selectedPersona.goals.map((g) => (
                            <li key={g} className="flex items-start gap-2 text-sm text-slate-700">
                              <span className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5 flex-shrink-0" />{g}
                            </li>
                          ))}
                        </ul>
                      ) : <p className="text-sm text-slate-400 italic">None listed.</p>}
                    </div>
                    {/* Pain Points */}
                    <div className="p-5">
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Pain Points</p>
                      {selectedPersona.painPoints.length > 0 ? (
                        <ul className="space-y-1">
                          {selectedPersona.painPoints.map((pp) => (
                            <li key={pp} className="flex items-start gap-2 text-sm text-slate-700">
                              <span className="w-1.5 h-1.5 rounded-full bg-red-400 mt-1.5 flex-shrink-0" />{pp}
                            </li>
                          ))}
                        </ul>
                      ) : <p className="text-sm text-slate-400 italic">None listed.</p>}
                    </div>
                  </div>

                  {/* Backstory panel */}
                  {selectedPersona.backstory && !!(selectedPersona.backstory.background || selectedPersona.backstory.traits?.length || selectedPersona.backstory.age) && (
                    <div className="p-5 border-t border-[#e2e8f0] bg-violet-50/50">
                      <p className="text-[10px] font-bold text-violet-500 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                        <Contact2 size={11} />Backstory
                      </p>
                      <div className="flex flex-wrap gap-x-5 gap-y-1 mb-3">
                        {selectedPersona.backstory.age && <span className="text-xs text-slate-600"><span className="font-semibold text-slate-500">Age:</span> {selectedPersona.backstory.age}</span>}
                        {selectedPersona.backstory.gender && <span className="text-xs text-slate-600"><span className="font-semibold text-slate-500">Gender:</span> {selectedPersona.backstory.gender}</span>}
                        {selectedPersona.backstory.education && <span className="text-xs text-slate-600"><span className="font-semibold text-slate-500">Education:</span> {selectedPersona.backstory.education}</span>}
                        {selectedPersona.backstory.familySituation && <span className="text-xs text-slate-600"><span className="font-semibold text-slate-500">Family:</span> {selectedPersona.backstory.familySituation}</span>}
                      </div>
                      {selectedPersona.backstory.background && (
                        <p className="text-sm text-slate-700 leading-relaxed mb-3">{selectedPersona.backstory.background}</p>
                      )}
                      {selectedPersona.backstory.traits?.length > 0 && (
                        <div className="flex flex-wrap gap-1.5">
                          {selectedPersona.backstory.traits.map(t => (
                            <span key={t} className="px-2.5 py-0.5 rounded-full bg-white border border-violet-200 text-violet-700 text-[11px] font-semibold">{t}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Stories panel */}
            <div className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">
              {/* Filter bar */}
              <div className="p-4 border-b border-[#e2e8f0] flex flex-wrap items-center gap-3">
                <div className="flex gap-1 bg-slate-100 rounded-lg p-1">
                  {(['all', 'high', 'medium', 'low'] as PriorityFilter[]).map(p => (
                    <button
                      key={p}
                      onClick={() => setPriorityFilter(p)}
                      className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all capitalize ${
                        priorityFilter === p ? 'bg-white shadow-sm text-navy' : 'text-slate-500 hover:text-slate-700'
                      }`}
                    >
                      {p === 'all' ? 'All' : p}
                    </button>
                  ))}
                </div>
                <div className="relative flex-1 min-w-[160px]">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Search stories..."
                    value={storySearch}
                    onChange={e => setStorySearch(e.target.value)}
                    className="w-full pl-8 pr-3 py-2 border border-[#e2e8f0] rounded-xl text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                  />
                </div>
                <span className="text-xs text-slate-400 ml-auto">{visibleStories.length} {visibleStories.length === 1 ? 'story' : 'stories'}</span>
              </div>

              {/* Stories list */}
              {visibleStories.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-center text-slate-400">
                  <FileText size={28} className="mb-3 opacity-40" />
                  <p className="text-sm">No stories match the current filters.</p>
                </div>
              ) : (
                <div className="divide-y divide-[#e2e8f0]">
                  {visibleStories.map(story => {
                    const style = PRIORITY_STYLES[story.priority] || PRIORITY_STYLES.medium;
                    return (
                      <div key={story.storyId} className="flex items-start gap-4 px-5 py-5 hover:bg-slate-50/60 transition-colors relative">
                        <div className={`absolute left-0 top-0 bottom-0 w-1 ${style.bar}`} />
                        <div className="flex-1 min-w-0 pl-2">
                          <div className="flex flex-wrap items-center gap-2 mb-1.5">
                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${style.badge}`}>
                              {story.priority}
                            </span>
                            {story.status && story.status !== 'draft' && (
                              <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                                story.status === 'accepted' ? 'bg-green-50 text-green-700 border border-green-200' :
                                story.status === 'rejected' ? 'bg-red-50 text-red-700 border border-red-200' :
                                'bg-blue-50 text-blue-700 border border-blue-200'
                              }`}>
                                {story.status}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-navy font-medium leading-relaxed">{story.story}</p>
                          {story.acceptanceCriteria.length > 0 && (
                            <p className="text-xs text-slate-400 mt-1">{story.acceptanceCriteria.length} acceptance criteria</p>
                          )}
                        </div>
                        {!selectedPersona && story.persona && (
                          <div
                            className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center text-[10px] font-bold text-slate-500 flex-shrink-0 ring-2 ring-white"
                            title={story.persona}
                          >
                            {getInitials(story.persona)}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

          </div>
        </div>
      )}

      {/* Fix 1: Assumptions & Specs lifted BELOW the two-column row so they're always reachable */}
      {!isEmpty && (techSpecItems.length > 0 || (data && data.assumptions.length > 0)) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {techSpecItems.length > 0 && (
            <div className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">
              <button
                onClick={() => setTechSpecsExpanded(!techSpecsExpanded)}
                className="w-full px-5 py-3.5 border-b border-[#e2e8f0] flex items-center justify-between bg-slate-50/50 hover:bg-slate-50 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <Settings size={15} className="text-slate-500" />
                  <span className="text-sm font-bold text-navy">Technical Specs</span>
                  <span className="text-xs text-slate-400">({techSpecItems.length})</span>
                </div>
                {techSpecsExpanded ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
              </button>
              {techSpecsExpanded && (
                <div className="p-5">
                  <ul className="space-y-2.5">
                    {techSpecItems.map((item) => (
                      <li key={item} className="flex items-start gap-3 text-sm text-slate-600">
                        <CheckCircle2 size={15} className="text-primary mt-0.5 flex-shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
          {data && data.assumptions.length > 0 && (
            <div className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">
              <button
                onClick={() => setAssumptionsExpanded(!assumptionsExpanded)}
                className="w-full px-5 py-3.5 border-b border-[#e2e8f0] flex items-center justify-between bg-slate-50/50 hover:bg-slate-50 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <Lightbulb size={15} className="text-amber-500" />
                  <span className="text-sm font-bold text-navy">Assumptions</span>
                  <span className="text-xs text-slate-400">({data.assumptions.length})</span>
                </div>
                {assumptionsExpanded ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
              </button>
              {assumptionsExpanded && (
                <div className="p-5">
                  <ul className="space-y-2.5">
                    {data.assumptions.map((item, i) => (
                      <li key={typeof item === 'string' ? item : `assumption-${i}`} className="flex items-start gap-3 text-sm text-slate-600">
                        <span className="w-1.5 h-1.5 mt-1.5 rounded-full bg-amber-400 flex-shrink-0" />
                        <span>{typeof item === 'string' ? item : (item as any).assumption || JSON.stringify(item)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Backstory modal */}
      {backstoryPersona && (
        <PersonaBackstoryModal
          sessionId={backstoryPersona.sessionId}
          persona={backstoryPersona}
          onSave={handleSaveBackstory}
          onClose={() => setBackstoryPersona(null)}
        />
      )}

      {/* Notification */}
      {notification && (
        <div className={`fixed top-20 right-5 px-4 py-3 rounded-xl text-sm font-medium shadow-lg z-[1000] ${
          notification.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'
        }`}>
          {notification.message}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// STAT CARD
// ============================================================================

const StatCard: React.FC<{ label: string; value: number; icon: React.ReactNode; iconColor: string }> = ({ label, value, icon, iconColor }) => (
  <div className="bg-white rounded-xl p-5 border border-[#e2e8f0] flex flex-col gap-1 relative overflow-hidden group">
    <div className={`absolute right-0 top-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity ${iconColor}`}>
      {icon}
    </div>
    <p className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">{label}</p>
    <p className="text-navy text-3xl font-bold">{value}</p>
  </div>
);
